import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { BehaviorSubject, Subject, fromEvent, merge } from 'rxjs';
import { filter, takeUntil, throttleTime } from 'rxjs/operators';
import { TabSessionService } from './tab-session.service';

export const INACTIVITY_DURATION_MS = 10 * 60 * 1000;
export const LOGOUT_COUNTDOWN_SECONDS = 5 * 60;
export const LOGOUT_COUNTDOWN_MS = LOGOUT_COUNTDOWN_SECONDS * 1000;

@Injectable({
  providedIn: 'root'
})
export class InactivityService {
  private readonly document = inject<Document>(DOCUMENT);
  private readonly router = inject(Router);
  private readonly tabSessionService = inject(TabSessionService);
  private stop$ = new Subject<void>();
  private intervalId?: number;
  private lastActivityAt = 0;
  private logoutDeadline = 0;
  private running = false;
  private loggingOut = false;

  readonly warningVisible$ = new BehaviorSubject<boolean>(false);
  readonly remainingSeconds$ = new BehaviorSubject<number>(LOGOUT_COUNTDOWN_SECONDS);

  start(): void {
    if (this.running) {
      return;
    }

    this.running = true;
    this.lastActivityAt = Date.now();
    this.logoutDeadline = 0;
    this.warningVisible$.next(false);
    this.remainingSeconds$.next(LOGOUT_COUNTDOWN_SECONDS);
    this.stop$ = new Subject<void>();

    merge(
      fromEvent(this.document, 'mousemove').pipe(throttleTime(250)),
      fromEvent(this.document, 'click'),
      fromEvent(this.document, 'keydown'),
      fromEvent(this.document, 'scroll', { capture: true }).pipe(throttleTime(250)),
      this.router.events.pipe(filter(event => event instanceof NavigationStart))
    ).pipe(takeUntil(this.stop$)).subscribe(() => this.recordActivity());

    fromEvent(this.document, 'visibilitychange').pipe(
      takeUntil(this.stop$)
    ).subscribe(() => this.checkInactivity());

    this.intervalId = window.setInterval(() => this.checkInactivity(), 1000);
  }

  stop(): void {
    if (!this.running) {
      return;
    }

    this.running = false;
    this.stop$.next();
    this.stop$.complete();

    if (this.intervalId !== undefined) {
      window.clearInterval(this.intervalId);
      this.intervalId = undefined;
    }

    this.warningVisible$.next(false);
    this.logoutDeadline = 0;
  }

  continueWorking(): void {
    if (!this.running || !this.warningVisible$.value) {
      return;
    }

    this.lastActivityAt = Date.now();
    this.logoutDeadline = 0;
    this.warningVisible$.next(false);
    this.remainingSeconds$.next(LOGOUT_COUNTDOWN_SECONDS);
  }

  logout(): void {
    if (this.loggingOut) {
      return;
    }

    this.loggingOut = true;
    this.stop();
    void this.tabSessionService.logout()
      .then(loggedOut => {
        if (!loggedOut) {
          this.start();
        }
      })
      .finally(() => {
        this.loggingOut = false;
      });
  }

  private recordActivity(): void {
    if (!this.running || this.warningVisible$.value) {
      return;
    }

    this.lastActivityAt = Date.now();
  }

  private checkInactivity(): void {
    if (!this.running) {
      return;
    }

    const now = Date.now();

    if (!this.warningVisible$.value) {
      if (now - this.lastActivityAt < INACTIVITY_DURATION_MS) {
        return;
      }

      this.logoutDeadline = this.lastActivityAt + INACTIVITY_DURATION_MS + LOGOUT_COUNTDOWN_MS;
      this.warningVisible$.next(true);
    }

    const remainingMilliseconds = Math.max(0, this.logoutDeadline - now);
    this.remainingSeconds$.next(Math.ceil(remainingMilliseconds / 1000));

    if (remainingMilliseconds === 0) {
      this.logout();
    }
  }
}
